import { jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import isEqual from "lodash/isEqual";
import { useLocation } from "react-router-dom";
import { usePrevious, useUpdateEffect } from "@react-hookz/web";
import { useLocalStorageSynced, useSessionStorage } from "@chronosphereio/core";
import { DEFAULT_TIME_RANGE, PinStatus, getDurationStringSeconds, isAbsoluteTimeRange, isRelativeTimeRange, toAbsoluteTimeRange } from "../../../core/index.js";
import { useInternalTimeRange, useSetRefreshIntervalParams } from "./query-params.js";
import { useAnalytics } from "../../model/analytics.js";
const TimeRangeContext = /*#__PURE__*/ createContext(void 0);
function useTimeRange() {
    const ctx = useContext(TimeRangeContext);
    if (void 0 === ctx) throw new Error('No TimeRangeContext found. Did you forget a Provider?');
    return ctx;
}
const PINNED_TIME_RANGE_KEY = 'CHRONOSPHERE_PINNED_TIME_RANGE';
const PINNED_TIME_RANGE_ACTIVE_KEY = 'CHRONOSPHERE_PINNED_TIME_RANGE_ACTIVE';
function TimeRangeProviderV1({ enableUrlParams: enabledURLParams = false, defaultTimeRange: initialTimeRange = DEFAULT_TIME_RANGE, initialRefreshInterval, children, enablePinning: enableTimeRangePinning = true, onTimeRangeChange, recomputeKey }) {
    const location = useLocation();
    const previousLocation = useRef(location);
    const pinStatusRef = useRef();
    const { timeRange, setTimeRange: setTimeRangeInternal } = useInternalTimeRange({
        initialTimeRange,
        enabledURLParams,
        recomputeKey
    });
    const prevTimeRange = usePrevious(timeRange);
    const [pinnedTimeRange, setPinnedTimeRangeInternal] = useLocalStorageSynced(PINNED_TIME_RANGE_KEY, {
        value: void 0,
        tabCount: 0,
        backup: void 0
    });
    const [pinnedTimeRangeActive, setPinnedTimeRangeActive] = useSessionStorage(PINNED_TIME_RANGE_ACTIVE_KEY, false);
    const pinStatus = useMemo(()=>{
        if (enableTimeRangePinning && pinnedTimeRange.tabCount > 0 && pinnedTimeRangeActive) {
            pinStatusRef.current = PinStatus.PINNED;
            return PinStatus.PINNED;
        }
        if (enableTimeRangePinning && pinnedTimeRange.tabCount > 0 && !pinnedTimeRangeActive) {
            pinStatusRef.current = PinStatus.PINNED_IN_DIFFERENT_TAB;
            return PinStatus.PINNED_IN_DIFFERENT_TAB;
        }
        pinStatusRef.current = PinStatus.UNPINNED;
        return PinStatus.UNPINNED;
    }, [
        enableTimeRangePinning,
        pinnedTimeRange.tabCount,
        pinnedTimeRangeActive
    ]);
    const setTimeRange = useCallback((value)=>{
        if (pinStatus === PinStatus.PINNED) setPinnedTimeRangeInternal({
            value: value,
            tabCount: pinnedTimeRange.tabCount,
            backup: {
                value: value,
                tabCount: pinnedTimeRange.tabCount
            }
        });
        setTimeRangeInternal(value, 'pushIn');
    }, [
        pinStatus,
        pinnedTimeRange.tabCount,
        setPinnedTimeRangeInternal,
        setTimeRangeInternal
    ]);
    useUpdateEffect(()=>{
        if (onTimeRangeChange && !isEqual(timeRange, prevTimeRange)) onTimeRangeChange(timeRange);
    }, [
        timeRange,
        onTimeRangeChange,
        prevTimeRange
    ]);
    const setPinnedTimeRange = useCallback((value)=>{
        setPinnedTimeRangeInternal({
            ...value,
            backup: value
        });
        if (value.tabCount > pinnedTimeRange.tabCount) setPinnedTimeRangeActive(true);
        else if (value.tabCount < pinnedTimeRange.tabCount) setPinnedTimeRangeActive(false);
    }, [
        pinnedTimeRange.tabCount,
        setPinnedTimeRangeActive,
        setPinnedTimeRangeInternal
    ]);
    const unpinTimeRange = useCallback(()=>{
        setPinnedTimeRange({
            ...pinnedTimeRange,
            tabCount: pinnedTimeRange.tabCount - 1
        });
    }, [
        pinnedTimeRange,
        setPinnedTimeRange
    ]);
    useEffect(()=>{
        if (pinStatus !== PinStatus.PINNED || !pinnedTimeRange.value) return;
        const pinnedValue = isAbsoluteTimeRange(pinnedTimeRange.value) ? {
            start: new Date(pinnedTimeRange.value.start),
            end: new Date(pinnedTimeRange.value.end)
        } : pinnedTimeRange.value;
        if (!isEqual(pinnedValue, timeRange)) setTimeRangeInternal(pinnedValue, 'replaceIn');
    }, [
        enableTimeRangePinning,
        pinStatus,
        pinnedTimeRange,
        setTimeRange,
        setTimeRangeInternal
    ]);
    useEffect(()=>{
        const handlePopState = (event)=>{
            if (void 0 === location || !event.state) return;
            if (location.pathname === previousLocation.current.pathname) {
                if (pinStatusRef.current === PinStatus.PINNED) unpinTimeRange();
            }
        };
        window.addEventListener('popstate', handlePopState);
        return ()=>{
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);
    const [refreshKey, setRefreshKey] = useState(0);
    const refresh = useCallback(()=>{
        setRefreshKey(refreshKey + 1);
    }, [
        refreshKey
    ]);
    const { refreshInterval, setRefreshInterval, refreshIntervalInMs } = useAutoRefresh({
        refresh,
        initialRefreshInterval,
        enabledURLParams
    });
    const ctx = useMemo(()=>{
        const isRelative = isRelativeTimeRange(timeRange);
        const absoluteTimeRange = isRelative ? toAbsoluteTimeRange(timeRange) : timeRange;
        const rangeMs = absoluteTimeRange.end.valueOf() - absoluteTimeRange.start.valueOf();
        return {
            isRelative,
            timeRange,
            setTimeRange,
            absoluteTimeRange,
            refresh,
            refreshKey: `${absoluteTimeRange.start}:${absoluteTimeRange.end}:${refreshInterval}:${refreshKey}:${recomputeKey}`,
            refreshInterval,
            refreshIntervalInMs,
            setRefreshInterval,
            rangeMs,
            pinnedTimeRange,
            setPinnedTimeRange,
            enableTimeRangePinning,
            pinStatus,
            unpinTimeRange
        };
    }, [
        timeRange,
        setTimeRange,
        refresh,
        refreshInterval,
        refreshKey,
        refreshIntervalInMs,
        setRefreshInterval,
        pinnedTimeRange,
        setPinnedTimeRange,
        enableTimeRangePinning,
        pinStatus,
        unpinTimeRange,
        recomputeKey
    ]);
    const analytics = useAnalytics();
    useEffect(()=>{
        analytics?.track('time_range_settings', {
            pinStatus: ctx.pinStatus
        });
    }, [
        analytics,
        ctx.pinStatus
    ]);
    return /*#__PURE__*/ jsx(TimeRangeContext.Provider, {
        value: ctx,
        children: children
    });
}
function useAutoRefresh({ initialRefreshInterval, refresh, enabledURLParams = false }) {
    const { refreshInterval, setRefreshInterval } = useSetRefreshIntervalParams({
        initialRefreshInterval,
        enabledURLParams
    });
    const refreshIntervalInSeconds = refreshInterval ? getDurationStringSeconds(refreshInterval) : null;
    const refreshIntervalInMs = refreshIntervalInSeconds ? 1000 * refreshIntervalInSeconds : null;
    useEffect(()=>{
        if (!refreshIntervalInMs) return;
        const intervalIdx = setInterval(()=>{
            refresh();
        }, refreshIntervalInMs);
        return ()=>{
            clearInterval(intervalIdx);
        };
    }, [
        refreshIntervalInMs,
        refresh
    ]);
    return {
        refreshInterval,
        setRefreshInterval,
        refreshIntervalInMs
    };
}
export { PINNED_TIME_RANGE_ACTIVE_KEY, PINNED_TIME_RANGE_KEY, TimeRangeContext, TimeRangeProviderV1, useAutoRefresh, useTimeRange };

//# sourceMappingURL=TimeRangeProvider.js.map